import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Calculator, Loader2, Sparkles } from "lucide-react";
import Select from "react-select";
import { publicQuoteService } from "../services/publicQuote.service";
import { useCountries } from "../hooks/useCountries";
import { useEligibleCountries } from "../hooks/useEligibleCountries";
import { safeApiErrorMessage } from "../utils/safeApiErrorMessage";

const FieldLabel = ({ children }) => (
  <label className="block text-[10px] uppercase tracking-[0.18em] font-semibold text-[#0A4730]/70 mb-2">
    {children}
  </label>
);

const INPUT_CLASS =
  "w-full rounded-2xl bg-white border border-slate-200 px-4 py-3.5 text-sm text-[#0A2540] placeholder:text-slate-400 focus:outline-none focus:border-[#0A4730] focus:ring-4 focus:ring-[#0A4730]/5 transition-all duration-200 shadow-sm";

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 50,
    borderRadius: 16,
    backgroundColor: "#FFFFFF",
    border: state.isFocused ? "1.5px solid #0A4730" : "1px solid #E2E8F0",
    boxShadow: state.isFocused
      ? "0 0 0 4px rgba(10, 71, 48, 0.08)"
      : "0 1px 2px rgba(0,0,0,0.03)",
    fontFamily: "inherit",
    fontSize: 14,
    paddingLeft: 4,
    cursor: "pointer",
    "&:hover": { borderColor: "#0A4730" },
    transition: "all 0.2s ease",
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 16,
    overflow: "hidden",
    zIndex: 999,
    boxShadow: "0 8px 30px rgba(10,71,48,0.12)",
    border: "1px solid #0A473015",
  }),
  menuList: (base) => ({ ...base, padding: 6, maxHeight: 220 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#0A4730"
      : state.isFocused
        ? "#F5F9F4"
        : "white",
    color: state.isSelected ? "white" : "#0A2540",
    borderRadius: 10,
    fontSize: 14,
    padding: "10px 12px",
    cursor: "pointer",
    transition: "background 0.1s",
  }),
  input: (base) => ({ ...base, fontSize: 14, color: "#0A2540" }),
  placeholder: (base) => ({
    ...base,
    fontSize: 14,
    color: "#0A254050",
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: 14,
    color: "#0A2540",
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.isFocused ? "#0A4730" : "#0A254040",
    transition: "color 0.15s, transform 0.2s",
    transform: state.selectProps.menuIsOpen
      ? "rotate(180deg)"
      : "rotate(0deg)",
    paddingRight: 12,
  }),
  noOptionsMessage: (base) => ({
    ...base,
    fontSize: 13,
    color: "#0A254060",
    padding: "12px 8px",
  }),
};

const SEND_CURRENCIES = [
  { value: "GBP", label: "British Pound(GBP)" },
  { value: "EUR", label: "EUR" },
  { value: "USD", label: "USD" },
];

const RECIPIENT_CURRENCIES = [
  { value: "KES", label: "Kenyan Shilling (KES)", country: "KE" },
  { value: "GHS", label: "Ghanaian Cedi (GHS)", country: "GH" },
  { value: "XAF", label: "Central African CFA (XAF)", country: "CM" },
];

export function FxCalculator() {
  const { countries } = useCountries();
  useEligibleCountries(countries);

  const [form, setForm] = useState({
    amount: "100",
    sendCurrency: "GBP",
    receiveCurrency: "KES",
    destinationCountry: "KE",
  });

  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState(null);
  const [loading, setLoading] = useState(false);

  const selectedSendOption = useMemo(
    () =>
      SEND_CURRENCIES.find((c) => c.value === form.sendCurrency) ||
      SEND_CURRENCIES[0],
    [form.sendCurrency],
  );

  const selectedReceiveOption = useMemo(
    () =>
      RECIPIENT_CURRENCIES.find((c) => c.value === form.receiveCurrency) ||
      RECIPIENT_CURRENCIES[0],
    [form.receiveCurrency],
  );

  // Live quoting engine with debounce
  const fetchLiveQuote = useCallback(async (currentForm) => {
    if (!currentForm.amount || Number(currentForm.amount) <= 0) {
      setQuote(null);
      setQuoteError(null);
      return;
    }

    setLoading(true);
    setQuoteError(null);

    try {
      const result = await publicQuoteService.preview({
        send_amount: currentForm.amount,
        send_currency: currentForm.sendCurrency,
        receive_currency: currentForm.receiveCurrency,
        destination_country: currentForm.destinationCountry,
      });

      setQuote(result);
    } catch (err) {
      setQuote(null);
      setQuoteError(
        safeApiErrorMessage(
          err,
          "We couldn't get a live rate right now. Please try again.",
        ),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchLiveQuote(form);
    }, 300);

    return () => clearTimeout(timer);
  }, [form, fetchLiveQuote]);

  return (
    <main className="min-h-screen bg-[#FDFDFD] px-4 py-12 text-left text-[#0A2540]">
      <div className="mx-auto max-w-xl">
        {/* Header Section */}
        <div className="mb-8 flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0A4730] text-white shadow-md shadow-[#0A4730]/20">
            <Calculator size={24} />
          </div>

          <div>
            <h1 className="text-2xl font-serif font-semibold text-[#0A4730] tracking-tight">
              Exchange Rate Calculator
            </h1>

            <p className="mt-1 text-sm text-slate-500">
              See exact live conversions instantly.
            </p>
          </div>
        </div>

        {/* Main Form Container */}
        <div className="space-y-4 rounded-3xl border border-slate-100 bg-white p-6 md:p-8 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)]">
          {/* You Send Section */}
          <div className="space-y-2">
            <FieldLabel>You send</FieldLabel>

            <div className="grid grid-cols-1 md:grid-cols-[1fr_170px] gap-3">
              <input
                type="number"
                min="1"
                step="0.01"
                value={form.amount}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    amount: e.target.value,
                  }))
                }
                className={INPUT_CLASS}
                placeholder="0.00"
              />

              <Select
                options={SEND_CURRENCIES}
                styles={selectStyles}
                value={selectedSendOption}
                onChange={(opt) => {
                  if (opt) {
                    setForm((prev) => ({
                      ...prev,
                      sendCurrency: opt.value,
                    }));
                  }
                }}
                isSearchable={false}
              />
            </div>
          </div>

          {/* INDICATIVE BRIDGE BAR (Live Rate & Fee Note) */}
          <div className="relative flex items-center justify-center py-2">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-100" />
            </div>

            <div className="relative flex items-center gap-2 rounded-2xl bg-[#F5F9F4] border border-[#0A4730]/15 px-4 py-2.5 text-xs text-[#0A4730] shadow-sm transition-all">
              {loading ? (
                <div className="flex items-center gap-2">
                  <Loader2
                    className="animate-spin text-[#0A4730]"
                    size={13}
                  />

                  <span className="font-medium">
                    Updating live rate...
                  </span>
                </div>
              ) : quoteError ? (
                <span className="text-red-500">
                  {quoteError}
                </span>
              ) : quote ? (
                <div className="flex items-center flex-wrap justify-center gap-2 text-center">
                  <span className="font-semibold">
                    1 {quote.send_currency} ={" "}
                    {Number(quote.exchange_rate).toFixed(4)}{" "}
                    {quote.receive_currency}
                  </span>

                  <span className="text-slate-300">
                    •
                  </span>

                  <span className="text-[#0A4730]/90 font-medium flex items-center gap-1">
                    <Sparkles
                      size={13}
                      className="text-[#0A4730]"
                    />
                    Our FX rate, no fees
                  </span>
                </div>
              ) : (
                <span className="text-slate-400">
                  Enter an amount to see live rates
                </span>
              )}
            </div>
          </div>

          {/* Recipient Currency Selection */}
          <div className="space-y-2">
            <FieldLabel>
              Recipient gets currency
            </FieldLabel>

            <Select
              options={RECIPIENT_CURRENCIES}
              styles={selectStyles}
              value={selectedReceiveOption}
              onChange={(opt) => {
                if (opt) {
                  setForm((prev) => ({
                    ...prev,
                    receiveCurrency: opt.value,
                    destinationCountry: opt.country,
                  }));
                }
              }}
              isSearchable={false}
            />
          </div>

          {/* Converted Output Display Field */}
          <div className="space-y-2 pt-2">
            <FieldLabel>
              Recipient gets amount (Estimated)
            </FieldLabel>

            <div className="flex items-center justify-between rounded-2xl bg-slate-50 border border-slate-200 px-4 py-3.5 text-sm font-semibold text-[#0A2540] shadow-sm">
              <span>
                {quote && !loading
                  ? Number(
                      quote.receive_amount,
                    ).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  : "0.00"}
              </span>

              <span className="text-[#0A4730] font-bold">
                {form.receiveCurrency}
              </span>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}